import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { bankMovementKeys } from './useBankMovements';

export const supplierLedgerKeys = {
  all: ['supplierLedger'],
  providers: (filters = {}) => [...supplierLedgerKeys.all, 'providers', filters],
  provider: (id) => [...supplierLedgerKeys.all, 'provider', id],
  invoices: (filters = {}) => [...supplierLedgerKeys.all, 'invoices', filters],
  adjustments: (filters = {}) => [...supplierLedgerKeys.all, 'adjustments', filters],
  cashAccounts: (filters = {}) => [...supplierLedgerKeys.all, 'cashAccounts', filters],
  reports: (kind, filters = {}) => [...supplierLedgerKeys.all, 'reports', kind, filters],
  accountingEntries: (filters = {}) => [...supplierLedgerKeys.all, 'accountingEntries', filters],
  aiStatus: () => [...supplierLedgerKeys.all, 'aiStatus'],
};

export const useSupplierLedgerProviders = (filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.providers(filters),
    queryFn: async () => {
      const response = await apiClient.get('supplier_ledger/providers', { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useSupplierLedgerProvider = (id, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.provider(id),
    queryFn: async () => {
      const response = await apiClient.get(`supplier_ledger/providers/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useSupplierInvoices = (filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.invoices(filters),
    queryFn: async () => {
      const response = await apiClient.get('supplier_invoices', { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateSupplierInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('supplier_invoices', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useUpdateSupplierInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`supplier_invoices/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useDeleteSupplierInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`supplier_invoices/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useCancelSupplierInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await apiClient.post(`supplier_invoices/${id}/cancel`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useUploadSupplierInvoiceAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`supplier_invoices/${invoiceId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
    },
  });
};

export const useSupplierInvoiceAttachmentSignedUrl = () =>
  useMutation({
    mutationFn: async ({ invoiceId, attachmentId }) => {
      const response = await apiClient.get(
        `supplier_invoices/${invoiceId}/attachments/${attachmentId}/signed-url`,
      );
      return response.data;
    },
  });

export const useCreateSupplierPaymentOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('supplier_ledger/payment-orders', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: ['paymentOrders'] });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
    },
  });
};

export const useSupplierAccountAdjustments = (filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.adjustments(filters),
    queryFn: async () => {
      const response = await apiClient.get('supplier_ledger/adjustments', { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateSupplierAccountAdjustment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('supplier_ledger/adjustments', payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useCancelSupplierAccountAdjustment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await apiClient.post(`supplier_ledger/adjustments/${id}/cancel`, { reason });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useImportSupplierOpeningBalances = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rows) => {
      const response = await apiClient.post('supplier_ledger/import/opening-balances', { rows });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useFinanceCashAccounts = (filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.cashAccounts(filters),
    queryFn: async () => {
      const response = await apiClient.get('finance_cash_accounts', { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateFinanceCashAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('finance_cash_accounts', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
    },
  });
};

export const useUpdateFinanceCashAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`finance_cash_accounts/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
    },
  });
};

export const useDeleteFinanceCashAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`finance_cash_accounts/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all });
      queryClient.invalidateQueries({ queryKey: bankMovementKeys.all });
    },
  });
};

export const useSupplierReports = (kind, filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.reports(kind, filters),
    queryFn: async () => {
      const response = await apiClient.get(`supplier_ledger/reports/${kind}`, { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useAccountingEntries = (filters = {}, options = {}) =>
  useQuery({
    queryKey: supplierLedgerKeys.accountingEntries(filters),
    queryFn: async () => {
      const response = await apiClient.get('accounting_entries', { params: filters });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateManualAccountingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('accounting_entries/manual', payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useUpdateManualAccountingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`accounting_entries/${id}`, data);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const usePostAccountingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`accounting_entries/${id}/post`, { status: 'posted' });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useCancelAccountingEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`accounting_entries/${id}/cancel`, { status: 'cancelled' });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierLedgerKeys.all }),
  });
};

export const useSupplierInvoiceAiStatus = () =>
  useQuery({
    queryKey: supplierLedgerKeys.aiStatus(),
    queryFn: async () => {
      const response = await apiClient.get('supplier_ledger/ai-extraction-status');
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
  });

export const downloadSupplierLedgerExport = async (kind = 'invoices') => {
  const response = await apiClient.get('supplier_ledger/export', {
    params: { kind },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${kind}_proveedores.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};
